import { ArrowRight, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';

interface ClinicalBottomActionBarProps {
  last_saved_at: number | null;
  editable: boolean;
  saving: boolean;
  /** null on tabs with no single "section" to batch-save (diagnósticos/recetas/labs/
   *  imagenología/adjuntos/addenda already save per-action) — hides the button pair. */
  on_save_section: (() => void) | null;
  on_save_and_continue: (() => void) | null;
}

function relativeSavedLabel(
  last_saved_at: number | null,
  now: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (last_saved_at == null) return t('clinical.last_saved_none');
  const seconds = Math.max(0, Math.floor((now - last_saved_at) / 1000));
  if (seconds < 60) return t('clinical.last_saved_seconds', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  return t('clinical.last_saved_minutes', { count: minutes });
}

/** Sticky footer for the active section — last-saved feedback ticks every second so
 *  "hace N segundos" stays accurate without the doctor refreshing anything. */
export function ClinicalBottomActionBar({
  last_saved_at,
  editable,
  saving,
  on_save_section,
  on_save_and_continue,
}: ClinicalBottomActionBarProps): JSX.Element {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, []);

  return (
    <div className="bg-card sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-2 rounded-b-lg border-t p-3">
      <p className="text-muted-foreground text-xs">{relativeSavedLabel(last_saved_at, now, t)}</p>
      {editable && on_save_section && on_save_and_continue ? (
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={on_save_section} disabled={saving}>
            <Save className="h-4 w-4" aria-hidden />
            <span>{t('clinical.save_section')}</span>
          </Button>
          <Button type="button" size="sm" onClick={on_save_and_continue} disabled={saving}>
            <span>{t('clinical.save_and_continue')}</span>
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
